import React, { useEffect, useState } from 'react';
import { Button, Input } from 'components';
import { DialogDescription, DialogFooter, DialogHeader } from 'components/dialog/dialog';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from 'analytics';
import { ANALYTICS_EVENTS } from 'analytics/events';
import { usePatientName } from 'hooks/usePatientName';
import { isAxiosError } from 'axios';

export interface ManualInputProps {
  value: string;
  error: string | null;
  onChange: (value: string) => void;
  onSubmit: () => void;
}

export const ManualInput: React.FC<ManualInputProps> = ({ value, error, onChange, onSubmit }) => {
  const { t } = useTranslation();
  const { trackEvent } = useAnalytics();
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { fetchPatientName } = usePatientName();

  const validatePatientId = (inputValue: string): string | null => {
    const trimmed = inputValue.trim();
    if (!trimmed) {
      return null;
    }
    if (!/^\d+$/.test(trimmed)) {
      return t('home.anamnesisLookupDialog.errorInvalidNumber');
    }
    const numValue = parseInt(trimmed, 10);
    if (numValue === 0 || isNaN(numValue)) {
      return t('home.anamnesisLookupDialog.errorNonZero');
    }
    return null;
  };

  const handleInputChange = (newValue: string) => {
    onChange(newValue);
    if (validationError) {
      setValidationError(null);
    }
    const error = validatePatientId(newValue);
    setValidationError(error);
  };

  const handleFormSubmit = async (event?: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event?.preventDefault();

    const trimmedValue = value.trim();

    if (!trimmedValue) {
      setValidationError(t('home.anamnesisLookupDialog.errorRequired'));
      return;
    }

    setIsSubmitting(true);
    try {
      await fetchPatientName(trimmedValue);
      trackEvent(ANALYTICS_EVENTS.HOME_START_CONSULTATION_MANUAL_PATIENT_ID);
      onSubmit();
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === 404) {
          setValidationError(t('home.anamnesisLookupDialog.errors.invalidPatientId'));
          return;
        }
      }
      setValidationError(t('home.anamnesisLookupDialog.errors.fetchingFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    setValidationError(null);
  }, []);

  return (
    <>
      <DialogHeader>
        <DialogDescription>{t('home.anamnesisLookupDialog.description')}</DialogDescription>
      </DialogHeader>
      <form
        onSubmit={(e) => {
          void handleFormSubmit(e);
        }}
        className="space-y-4"
      >
        <div className="space-y-2">
          <Input
            autoFocus
            id="patient-id"
            name="patient-id"
            value={value}
            onChange={(e) => handleInputChange(e.target.value)}
            placeholder={t('home.anamnesisLookupDialog.placeholder')}
            autoComplete="off"
            type="text"
            inputMode="numeric"
            disabled={isSubmitting}
            aria-describedby="patient-id-error"
          />
          {error ? (
            <p role="alert" className="text-sm text-red-500">
              {error}
            </p>
          ) : null}
          {validationError ? (
            <p id="patient-id-error" className="text-sm text-red-500">
              {validationError}
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <Button
            type="submit"
            fullWidth
            data-ph-no-autocapture
            disabled={!!validationError || isSubmitting}
          >
            {isSubmitting
              ? t('home.anamnesisLookupDialog.startingConversation')
              : t('home.anamnesisLookupDialog.startConversation')}
          </Button>
        </DialogFooter>
      </form>
    </>
  );
};
